import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const markdownComponents = {
	a: ( props ) => <a { ...props } target="_blank" rel="noopener noreferrer" className="od-underline od-text-[var(--od-chatbot-link-color,#ff1e0b)]" style={ { lineBreak: 'anywhere' } } />,
	p: ( props ) => <p { ...props } className="last:od-mb-0 od-mb-2" />,
	ul: ( props ) => <ul { ...props } className="last:od-mb-0 od-mb-2 od-pl-5 od-list-disc" />,
	ol: ( props ) => <ol { ...props } className="last:od-mb-0 od-mb-2 od-pl-5 od-list-decimal" />,
	li: ( props ) => <li { ...props } className="last:od-mb-0 od-mb-1" />,
	strong: ( props ) => <strong { ...props } className="od-font-bold" />,
	code: ( props ) => <code { ...props } className="od-bg-white/10 od-px-1.5 od-py-px od-rounded od-text-[0.9em]" />,
	pre: ( props ) => <pre { ...props } className="od-bg-black/30 od-p-2 od-rounded-md od-overflow-x-auto od-my-1" />,
};

function UserIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className="od-w-3 od-h-3 od-fill-current">
			<path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.4 0-8 2.2-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.8-3.6-5-8-5z" />
		</svg>
	);
}

function BotIcon( { large } ) {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className={ large ? 'od-w-[26px] od-h-[26px] od-fill-current' : 'od-w-3 od-h-3 od-fill-current' }>
			<path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-4.6 3.8A1 1 0 0 1 3 20V5a1 1 0 0 1 1-1z" />
		</svg>
	);
}

function formatTime() {
	try {
		return new Date().toLocaleTimeString( [], { hour: '2-digit', minute: '2-digit' } );
	} catch ( e ) {
		return '';
	}
}

// Stable per-browser identity for the message-remaining-this-session counter.
// Without this, the server falls back to IP address, which multiple visitors
// behind the same office NAT / mobile carrier CGNAT would all share —
// causing the "remaining" count to jump unexpectedly as others behind the
// same IP send messages this browser doesn't know about.
function getSessionToken() {
	try {
		var storageKey = 'odChatbotSessionToken';
		var token = window.localStorage.getItem( storageKey );

		if ( ! token ) {
			token = ( window.crypto && window.crypto.randomUUID )
				? window.crypto.randomUUID()
				: ( Date.now().toString( 36 ) + Math.random().toString( 36 ).slice( 2 ) );
			window.localStorage.setItem( storageKey, token );
		}

		return token;
	} catch ( e ) {
		return ''; // localStorage unavailable (e.g. private browsing) — server falls back to IP.
	}
}

function Avatar( { isUser, chatbotAvatar } ) {
	if ( ! isUser && chatbotAvatar ) {
		return <img className="od-w-6 od-h-6 od-rounded-full od-shrink-0 od-object-cover" src={ chatbotAvatar } alt="" />;
	}

	return (
		<span className="od-w-6 od-h-6 od-rounded-full od-shrink-0 od-flex od-items-center od-justify-center od-bg-[#2a2a2a] od-text-[#99a1af]">
			{ isUser ? <UserIcon /> : <BotIcon /> }
		</span>
	);
}

function MessageBubble( { message, config } ) {
	const isUser = 'user' === message.type;
	const bubbleColor = {
		user: 'od-bg-[var(--od-chatbot-user-message-color,#ff1e0b)] od-text-white',
		bot: 'od-bg-[var(--od-chatbot-bot-message-color,#232323)] od-text-[var(--od-chatbot-font-color,#99a1af)]',
		error: 'od-bg-[rgba(255,30,11,0.12)] od-text-[#ff8a80]',
	}[ message.type ];

	return (
		<div className={ 'od-flex od-items-end od-gap-2 od-max-w-[90%] od-animate-[od-chatbot-msg-in_0.18s_ease] ' + ( isUser ? 'od-flex-row-reverse od-self-end' : 'od-self-start' ) }>
			<Avatar isUser={ isUser } chatbotAvatar={ config.chatbotAvatar } />
			<div className={ 'od-flex od-flex-col od-min-w-0 ' + ( isUser ? 'od-items-end' : 'od-items-start' ) }>
				<div className="od-flex od-gap-1.5 od-text-[0.75em] od-mb-1 od-whitespace-nowrap">
					<span className="od-text-white od-font-semibold">{ isUser ? config.strings.you : ( config.chatbotName || config.strings.assistant ) }</span>
					<span className="od-text-white/55">{ message.time }</span>
				</div>
				<div className={ 'od-py-2 od-px-3 od-rounded-xl od-leading-relaxed od-break-words ' + bubbleColor }>
					{ message.html
						// Admin-authored WYSIWYG content (the Welcome Message
						// setting) — real HTML, not markdown, so it needs to
						// render as-is rather than through ReactMarkdown (which
						// doesn't interpret raw HTML tags by default).
						? <div dangerouslySetInnerHTML={ { __html: message.text } } />
						: ( 'bot' === message.type
							? <ReactMarkdown remarkPlugins={ [ remarkGfm ] } components={ markdownComponents }>{ message.text }</ReactMarkdown>
							: message.text ) }
				</div>
			</div>
		</div>
	);
}

function TypingIndicator( { chatbotAvatar } ) {
	return (
		<div className="od-flex od-items-end od-gap-2 od-max-w-[90%] od-self-start">
			<Avatar isUser={ false } chatbotAvatar={ chatbotAvatar } />
			<div className="od-flex od-items-center od-gap-1 od-py-2.5 od-px-3 od-rounded-xl od-bg-[#232323]">
				<span className="od-chatbot-typing-dot od-w-1.5 od-h-1.5 od-rounded-full od-bg-current od-opacity-40 od-animate-[od-chatbot-typing-bounce_1.2s_infinite]"></span>
				<span className="od-chatbot-typing-dot od-w-1.5 od-h-1.5 od-rounded-full od-bg-current od-opacity-40 od-animate-[od-chatbot-typing-bounce_1.2s_infinite]"></span>
				<span className="od-chatbot-typing-dot od-w-1.5 od-h-1.5 od-rounded-full od-bg-current od-opacity-40 od-animate-[od-chatbot-typing-bounce_1.2s_infinite]"></span>
			</div>
		</div>
	);
}

export default function ChatWidget( { config } ) {
	const [ isOpen, setIsOpen ] = useState( false );
	const [ hasOpenedBefore, setHasOpenedBefore ] = useState( false );
	const [ messages, setMessages ] = useState( [] );
	const [ remaining, setRemaining ] = useState( null );
	const [ inputValue, setInputValue ] = useState( '' );
	const [ isSending, setIsSending ] = useState( false );
	const [ isTyping, setIsTyping ] = useState( false );
	const [ showWelcomePrompt, setShowWelcomePrompt ] = useState( false );

	const inputRef = useRef( null );
	const messagesRef = useRef( null );

	// Auto-dismissing teaser bubble near the trigger button — shown once per
	// page load, before the visitor has opened the chat, then hidden again on
	// its own so it doesn't linger indefinitely.
	useEffect( () => {
		if ( ! config.welcomePrompt || ! config.welcomePrompt.trim() ) {
			return;
		}

		setShowWelcomePrompt( true );

		const timer = window.setTimeout( function () {
			setShowWelcomePrompt( false );
		}, 4000 );

		return function () {
			window.clearTimeout( timer );
		};
	}, [] );

	// Rolling conversation summary + how many user/bot turns have already
	// been folded into it — refs (not state) since updating them should
	// never trigger a re-render, and they're read only at send-time. Never
	// sent anywhere except the /summarize and /message requests themselves —
	// this is the only copy that exists, gone when the tab closes.
	const summaryRef = useRef( '' );
	const foldedCountRef = useRef( 0 );

	useEffect( () => {
		if ( messagesRef.current ) {
			messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
		}
	}, [ messages, isTyping ] );

	function appendMessage( text, type, isHtml ) {
		setMessages( ( prev ) => [ ...prev, { text: text, type: type, time: formatTime(), html: !! isHtml } ] );
	}

	function fetchSessionStatus() {
		if ( ! config.sessionUrl ) {
			return;
		}

		var token = getSessionToken();
		var url   = token
			? config.sessionUrl + ( -1 === config.sessionUrl.indexOf( '?' ) ? '?' : '&' ) + 'session_token=' + encodeURIComponent( token )
			: config.sessionUrl;

		fetch( url )
			.then( function ( response ) { return response.json(); } )
			.then( function ( data ) {
				if ( data && 'number' === typeof data.remaining ) {
					setRemaining( data.remaining );
				}
			} )
			.catch( function () {} );
	}

	function handleToggle() {
		const next = ! isOpen;
		setIsOpen( next );
		setShowWelcomePrompt( false );

		if ( next ) {
			window.setTimeout( function () {
				if ( inputRef.current ) {
					inputRef.current.focus();
				}
			}, 0 );

			if ( ! hasOpenedBefore ) {
				setHasOpenedBefore( true );

				if ( config.welcomeMessage ) {
					appendMessage( config.welcomeMessage, 'bot', true );
				}

				fetchSessionStatus();
			}
		}
	}

	function buildHistory() {
		const depth = config.historyDepth || 0;
		if ( depth <= 0 ) {
			return [];
		}

		// Only user/bot turns carry conversation meaning to the AI — an
		// 'error' bubble (network failure, rate limit, etc.) was never
		// actually said by either side, so it's excluded rather than
		// resent as if it were part of the exchange.
		return messages
			.filter( ( m ) => 'user' === m.type || 'bot' === m.type )
			.slice( -depth )
			.map( ( m ) => ( { role: 'user' === m.type ? 'user' : 'assistant', content: m.text } ) );
	}

	// Folds turns that just fell outside the History Depth window into the
	// rolling summary, in the background — fired-and-forgotten so it never
	// adds latency to the message the visitor is actually waiting on. If it
	// fails, foldedCountRef is left untouched so those turns are retried on
	// the next message rather than silently lost.
	function maybeSummarize( userBotMessages ) {
		const depth = config.historyDepth || 0;
		if ( ! config.summarizationEnabled || depth <= 0 ) {
			return;
		}

		const overflowEnd = userBotMessages.length - depth;
		if ( overflowEnd <= foldedCountRef.current ) {
			return; // Nothing new has fallen out of the window since the last fold.
		}

		const toFold = userBotMessages
			.slice( foldedCountRef.current, overflowEnd )
			.map( ( m ) => ( { role: 'user' === m.type ? 'user' : 'assistant', content: m.text } ) );

		if ( ! toFold.length ) {
			return;
		}

		fetch( config.summarizeUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': config.nonce,
			},
			body: JSON.stringify( { existing_summary: summaryRef.current, messages_to_fold: toFold } ),
		} )
			.then( function ( response ) {
				return response.json().then( function ( data ) {
					return { ok: response.ok, data: data };
				} );
			} )
			.then( function ( result ) {
				if ( result.ok && result.data && 'string' === typeof result.data.summary ) {
					summaryRef.current = result.data.summary;
					foldedCountRef.current = overflowEnd;
				}
			} )
			.catch( function () {} );
	}

	function handleSubmit( e ) {
		e.preventDefault();

		const message = inputValue.trim();
		if ( ! message || isSending ) {
			return;
		}

		const history = buildHistory();
		const userBotMessages = messages.filter( ( m ) => 'user' === m.type || 'bot' === m.type );

		appendMessage( message, 'user' );
		setInputValue( '' );
		setIsSending( true );
		setIsTyping( true );

		maybeSummarize( userBotMessages );

		fetch( config.restUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': config.nonce,
			},
			body: JSON.stringify( { message: message, history: history, summary: summaryRef.current, session_token: getSessionToken() } ),
		} )
			.then( function ( response ) {
				return response.json().then( function ( data ) {
					return { ok: response.ok, data: data };
				} );
			} )
			.then( function ( result ) {
				setIsTyping( false );

				if ( result.ok && result.data && result.data.reply ) {
					appendMessage( result.data.reply, 'bot' );

					if ( 'number' === typeof result.data.remaining ) {
						setRemaining( result.data.remaining );
					}
				} else {
					appendMessage( ( result.data && result.data.message ) || config.strings.errorGeneric, 'error' );
				}
			} )
			.catch( function () {
				setIsTyping( false );
				appendMessage( config.strings.errorNetwork, 'error' );
			} )
			.finally( function () {
				setIsSending( false );
			} );
	}

	const isLeft = 'left' === config.widgetPosition;
	const sideClass = isLeft ? 'od-left-5' : 'od-right-5';
	const originClass = isLeft ? 'od-origin-bottom-left' : 'od-origin-bottom-right';
	// wp_localize_script() stringifies booleans ("" for false, "1" for true),
	// so a strict comparison against the real boolean `false` never matches —
	// a truthy check is required, same as config.summarizationEnabled above.
	const shadowEnabled = !! config.widgetShadowEnabled;
	const triggerShadowClass = shadowEnabled
		? 'od-shadow-[0_4px_16px_rgba(0,0,0,0.35)] hover:od-shadow-[0_6px_20px_rgba(0,0,0,0.4)]'
		: '';
	const windowShadowClass = shadowEnabled ? 'od-shadow-[0_16px_48px_rgba(0,0,0,0.45)]' : '';

	const style = {
		'--od-chatbot-header-color': config.headerColor,
		'--od-chatbot-header-title-color': config.headerTitleColor,
		'--od-chatbot-window-bg': config.windowBgColor,
		'--od-chatbot-trigger-color': config.triggerColor,
		'--od-chatbot-button-color': config.buttonColor,
		'--od-chatbot-button-text-color': config.buttonTextColor,
		'--od-chatbot-link-color': config.linkColor,
		'--od-chatbot-link-hover-color': config.linkHoverColor,
		'--od-chatbot-user-message-color': config.userMsgColor,
		'--od-chatbot-bot-message-color': config.botMsgColor,
		'--od-chatbot-input-bg-color': config.inputBgColor,
		'--od-chatbot-input-text-color': config.inputTextColor,
		'--od-chatbot-input-focus-color': config.inputFocusColor,
		'--od-chatbot-font-color': config.fontColor,
		fontSize: config.fontSize + 'px',
		color: config.fontColor,
		fontFamily: config.fontFamily,
	};

	const remainingText = 1 === remaining
		? config.strings.remainingOne
		: config.strings.remainingMany.replace( '%d', remaining );

	return (
		<div id="od-chatbot-widget" className="od-fixed od-z-[999999]" style={ style }>
			{ showWelcomePrompt && config.welcomePrompt && (
				<div
					id="od-chatbot-welcome-prompt"
					className={ 'od-fixed od-bottom-[76px] od-max-w-[220px] od-bg-white od-text-[#161616] od-text-sm od-leading-snug od-py-2.5 od-px-3.5 od-rounded-xl od-shadow-[0_6px_20px_rgba(0,0,0,0.25)] od-animate-[od-chatbot-msg-in_0.18s_ease] ' + sideClass }
					role="status"
					dangerouslySetInnerHTML={ { __html: config.welcomePrompt } }
				/>
			) }

			<button
				id="od-chatbot-toggle"
				type="button"
				aria-label={ isOpen ? config.strings.closeChat : config.strings.openChat }
				onClick={ handleToggle }
				className={ 'od-fixed od-bottom-5 od-w-14 od-h-14 od-rounded-full od-border-0 od-bg-[var(--od-chatbot-trigger-color,#ff1e0b)] od-text-white od-cursor-pointer od-flex od-items-center od-justify-center od-overflow-hidden od-p-0 od-transition od-duration-150 od-ease-in-out hover:od-scale-[1.06] active:od-scale-[0.96] ' + triggerShadowClass + ' ' + sideClass }
			>
				{ config.toggleIconUrl
					? <img className="od-w-full od-h-full od-object-cover" src={ config.toggleIconUrl } alt="" />
					: <BotIcon large /> }
			</button>

			<div
				id="od-chatbot-window"
				className={ ( isOpen ? '' : 'od-hidden' ) + ' od-fixed od-bottom-[88px] od-w-[360px] od-max-w-[calc(100vw-40px)] od-h-[480px] od-max-h-[calc(100vh-120px)] od-bg-[var(--od-chatbot-window-bg,#161616)] od-rounded-2xl od-flex od-flex-col od-overflow-hidden ' + windowShadowClass + ' ' + sideClass + ' ' + originClass }
				role="dialog"
				aria-modal="true"
				aria-hidden={ ! isOpen }
				aria-label={ config.strings.dialogLabel }
			>
				<div className="od-bg-[var(--od-chatbot-header-color,#161616)] od-text-[var(--od-chatbot-header-title-color,#ffffff)] od-py-3.5 od-px-[18px] od-flex od-items-center od-justify-between od-font-semibold od-border-b od-border-white/10">
					<div className="od-flex od-items-center od-gap-2.5">
						{ config.widgetLogoUrl && <img className="od-w-[26px] od-h-[26px] od-rounded-full od-object-cover" src={ config.widgetLogoUrl } alt="" /> }
						<span>{ config.chatbotName }</span>
					</div>
					<button
						id="od-chatbot-close"
						type="button"
						aria-label={ config.strings.closeChat }
						onClick={ () => setIsOpen( false ) }
						className="od-bg-transparent od-border-0 od-text-[var(--od-chatbot-header-title-color,#ffffff)] od-text-xl od-leading-none od-cursor-pointer od-opacity-75 od-transition-opacity od-duration-150 hover:od-opacity-100"
					>
						&times;
					</button>
				</div>

				<div id="od-chatbot-messages" ref={ messagesRef } className="od-flex-1 od-overflow-y-auto od-p-4 od-flex od-flex-col od-gap-3.5">
					{ messages.map( ( message, index ) => <MessageBubble key={ index } message={ message } config={ config } /> ) }
					{ isTyping && <TypingIndicator chatbotAvatar={ config.chatbotAvatar } /> }
				</div>

				{ null !== remaining && (
					<div id="od-chatbot-remaining" className="od-text-[0.75em] od-text-[var(--od-chatbot-font-color,#99a1af)] od-opacity-85 od-text-center od-pt-1.5 od-px-2.5">
						{ remainingText }
					</div>
				) }

				<form id="od-chatbot-form" onSubmit={ handleSubmit } className="od-flex od-gap-2.5 od-p-3 od-border-t od-border-white/10">
					<textarea
						id="od-chatbot-input"
						ref={ inputRef }
						rows="1"
						maxLength={ config.maxInputChars }
						placeholder={ config.strings.placeholder }
						value={ inputValue }
						onChange={ ( e ) => setInputValue( e.target.value ) }
						className="od-flex-1 od-resize-none od-bg-[var(--od-chatbot-input-bg-color,#1f1f1f)] od-border od-border-white/10 od-rounded-[10px] od-py-2 od-px-3 od-text-inherit od-font-inherit od-text-[var(--od-chatbot-input-text-color,#f5f5f5)] od-max-h-20 od-transition-[border-color] od-duration-150 focus:od-outline-none focus:od-border-[var(--od-chatbot-input-focus-color,#ff1e0b)] placeholder:od-text-[#99a1af]"
					/>
					<button
						id="od-chatbot-send"
						type="submit"
						disabled={ isSending }
						className="od-bg-[var(--od-chatbot-button-color,#ff1e0b)] od-text-[var(--od-chatbot-button-text-color,#ffffff)] od-border-0 od-rounded-[10px] od-px-4 od-cursor-pointer od-transition-transform od-duration-150 enabled:hover:od-brightness-110 enabled:active:od-scale-[0.97] disabled:od-opacity-50 disabled:od-cursor-default"
					>
						{ config.strings.send }
					</button>
				</form>
			</div>
		</div>
	);
}
